// Daily messaging limit of a WhatsApp channel: the single source for what the
// campaign wizard displays, compares against and sends.

export const MESSAGING_LIMIT_KIND = Object.freeze({
  LIMITED: "limited",
  UNLIMITED: "unlimited",
  UNKNOWN: "unknown",
});

// Sent as `currentMessageLimit` for an unlimited tier. The server only accepts
// finite positive integers, so "no limit" travels as a number larger than any
// audience it allows.
export const UNLIMITED_CAMPAIGN_MESSAGE_LIMIT = 1000000;

// Mirrors TIER_LIMITS in the backend file
// src/controllers/broadcast/campaignMessageLimit.service.ts. Keep both tables
// in sync: a tier missing here is shown as "No disponible" and is not sent.
const TIER_LIMITS = Object.freeze({
  TIER_50: 50,
  TIER_250: 250,
  "TIER_0.25K": 250,
  TIER_1K: 1000,
  TIER_2K: 2000,
  TIER_10K: 10000,
  TIER_100K: 100000,
  UNTIERED: 250,
  TIER_UNLIMITED: MESSAGING_LIMIT_KIND.UNLIMITED,
});

// Pinned so the separators do not change with the locale of the browser.
const limitFormatter = new Intl.NumberFormat("en-US");

const isLimited = (limit) =>
  limit?.kind === MESSAGING_LIMIT_KIND.LIMITED &&
  Number.isInteger(limit.value) &&
  limit.value > 0;

const isUnlimited = (limit) => limit?.kind === MESSAGING_LIMIT_KIND.UNLIMITED;

/**
 * Turns the raw `current_limit` tier of the status response into a limit the
 * wizard can use. Anything that is not a known tier is unknown.
 *
 * @param {unknown} currentLimit
 * @returns {{ kind: "limited", value: number } | { kind: "unlimited" } | { kind: "unknown" }}
 */
export const resolveMessagingLimit = (currentLimit) => {
  if (typeof currentLimit !== "string") {
    return { kind: MESSAGING_LIMIT_KIND.UNKNOWN };
  }

  const tier = currentLimit.trim().toUpperCase();
  // Own keys only: an inherited key must never be read as a tier.
  if (!Object.prototype.hasOwnProperty.call(TIER_LIMITS, tier)) {
    return { kind: MESSAGING_LIMIT_KIND.UNKNOWN };
  }

  const tierLimit = TIER_LIMITS[tier];
  if (tierLimit === MESSAGING_LIMIT_KIND.UNLIMITED) {
    return { kind: MESSAGING_LIMIT_KIND.UNLIMITED };
  }
  return { kind: MESSAGING_LIMIT_KIND.LIMITED, value: tierLimit };
};

/**
 * Text shown on screen for a resolved limit, or `null` when there is nothing
 * truthful to show.
 */
export const formatMessagingLimit = (limit) => {
  if (isLimited(limit)) return limitFormatter.format(limit.value);
  if (isUnlimited(limit)) return "Ilimitado";
  return null;
};

/**
 * Value of `currentMessageLimit` in the create-campaign request. This is the
 * only place that decides it. The result is either `undefined` (the caller
 * must leave the key out) or a finite positive integer, never `null`, `0`,
 * `NaN`, `Infinity`, a negative number or a string.
 */
export const toCampaignMessageLimit = (limit) => {
  if (isLimited(limit)) return limit.value;
  if (isUnlimited(limit)) return UNLIMITED_CAMPAIGN_MESSAGE_LIMIT;
  return undefined;
};

/**
 * Whether a campaign for `recipientCount` contacts goes over the daily limit.
 * Only a known numeric limit can be exceeded.
 */
export const exceedsMessagingLimit = (recipientCount, limit) =>
  isLimited(limit) && recipientCount > limit.value;
